import { Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { ArrowLeft, Play, Trophy, Coins, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { beep } from "@/embed/_gameKit";
import { BalloonField } from "./BalloonScene";
import { ResultCard } from "./ResultCard";

const ROUND = 60;
const BASE = { normal: 10, gold: 50 };

function Sky() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,#120a3a 0%,#3b1a6b 45%,#ff6f6f 85%,#ffb36b 100%)" }} />
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="pz-cloud absolute rounded-full bg-white/10 blur-2xl"
          style={{ width: 260 + i * 80, height: 90 + i * 20, top: `${12 + i * 19}%`, left: "-30%", animationDuration: `${38 + i * 14}s`, animationDelay: `${-i * 9}s` }} />
      ))}
    </div>
  );
}

function Hud({ score, left, combo, flash }) {
  return (
    <div className="absolute top-0 inset-x-0 z-10 flex items-start justify-between p-4 pointer-events-none">
      <div className="pz-chip" data-testid="bp-score"><span className="pz-chip-label">Score</span><span className="pz-chip-val">{score.toLocaleString()}</span></div>
      <div className={`pz-chip ${left <= 10 ? "text-[#FF2E88] animate-pulse" : ""}`} data-testid="bp-timer"><span className="pz-chip-label">Time</span><span className="pz-chip-val">{left}s</span></div>
      <div className={`pz-chip transition-transform duration-150 ${combo > 1 ? "scale-110 text-[#FFB800]" : ""}`} data-testid="bp-combo"><span className="pz-chip-label">Combo</span><span className="pz-chip-val">x{combo}</span></div>
      {flash && <div className="fixed inset-0 bg-[#ff1e3c]/25 pointer-events-none" />}
    </div>
  );
}

export default function BalloonPop() {
  const { user, refresh } = useAuth();
  const [phase, setPhase] = useState("ready");
  const [count, setCount] = useState(0);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [left, setLeft] = useState(ROUND);
  const [combo, setCombo] = useState(1);
  const [flash, setFlash] = useState(false);
  const [result, setResult] = useState(null);
  const [sound, setSound] = useState(true);
  const running = useRef(false);
  const startedAt = useRef(0);
  const st = useRef({});
  const session = useRef(null);

  useEffect(() => { document.title = "Jixxeo Balloon Pop"; return () => { running.current = false; }; }, []);

  const start = async () => {
    session.current = null;
    if (user) { try { session.current = (await api.post("/playzone/balloon/start")).data.session_id; } catch { session.current = null; } }
    st.current = { score: 0, pops: 0, golden: 0, bombs: 0, combo: 1, maxCombo: 1, last: 0 };
    setScore(0); setCombo(1); setLeft(ROUND); setResult(null);
    setRound((r) => r + 1);
    setPhase("count");
    for (let i = 3; i > 0; i--) { setCount(i); if (sound) beep(440, 0.12, "triangle"); await new Promise((r) => setTimeout(r, 800)); }
    if (sound) beep(880, 0.2, "triangle");
    startedAt.current = performance.now();
    running.current = true;
    setPhase("playing");
  };

  useEffect(() => {
    if (phase !== "playing") return;
    const t = setInterval(() => {
      const l = Math.max(0, ROUND - Math.floor((performance.now() - startedAt.current) / 1000));
      setLeft(l);
      if (l <= 0) { clearInterval(t); finish(); }
    }, 200);
    return () => clearInterval(t);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = async () => {
    running.current = false;
    setPhase("over");
    const s = st.current;
    const base = { score: s.score, pops: s.pops, golden: s.golden, bombs: s.bombs, max_combo: s.maxCombo };
    if (!session.current) return setResult({ ...base, guest: !user });
    try {
      const { data } = await api.post("/playzone/balloon/finish", { session_id: session.current, ...base });
      setResult({ ...base, ...data });
      refresh?.();
    } catch (e) {
      setResult({ ...base, error: e?.response?.data?.detail || "Couldn't save this round" });
    }
  };

  const onPop = (b) => {
    const s = st.current;
    const now = performance.now();
    if (b.kind === "bomb") {
      s.score = Math.max(0, s.score - 30); s.combo = 1; s.bombs += 1;
      setFlash(true); setTimeout(() => setFlash(false), 180);
      if (sound) beep(120, 0.3, "sawtooth", 0.18);
      navigator.vibrate?.([40, 30, 40]);
    } else {
      s.combo = now - s.last < 1100 ? Math.min(5, s.combo + 1) : 1;
      s.last = now; s.pops += 1;
      if (b.kind === "gold") s.golden += 1;
      s.maxCombo = Math.max(s.maxCombo, s.combo);
      s.score += BASE[b.kind] * s.combo;
      if (sound) beep(b.kind === "gold" ? 1320 : 520 + s.combo * 110, 0.09, "sine", 0.14);
      navigator.vibrate?.(12);
    }
    setScore(s.score); setCombo(s.combo);
  };
  const onEscape = (b) => { if (b.kind !== "bomb") { st.current.combo = 1; setCombo(1); } };

  return (
    <div className="fixed inset-0 select-none text-white overflow-hidden pz-root" data-testid="balloon-pop">
      <Sky />
      <Canvas className="!absolute inset-0" camera={{ position: [0, 0, 10], fov: 50 }} dpr={[1, 2]} gl={{ alpha: true, antialias: true }} style={{ touchAction: "none" }}>
        <ambientLight intensity={0.55} />
        <hemisphereLight args={["#ffe9f5", "#3b1a6b", 0.7]} />
        <directionalLight position={[4, 6, 6]} intensity={1.6} />
        <pointLight position={[-5, -3, 4]} intensity={18} color="#00E5FF" />
        <Suspense fallback={null}>
          {phase === "playing" && <BalloonField key={round} running={running} startedAt={startedAt} onPop={onPop} onEscape={onEscape} />}
        </Suspense>
      </Canvas>
      {phase === "playing" && <Hud score={score} left={left} combo={combo} flash={flash} />}
      <div className="absolute bottom-4 left-4 z-20 flex gap-2">
        <Link to="/playzone" className="pz-icon-btn" aria-label="Back to PlayZone" data-testid="bp-back"><ArrowLeft className="w-5 h-5" /></Link>
        <button onClick={() => setSound(!sound)} className="pz-icon-btn" aria-label="Toggle sound" data-testid="bp-sound">{sound ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}</button>
      </div>
      {phase === "count" && <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"><div key={count} className="pz-count" data-testid="bp-countdown">{count}</div></div>}
      {phase === "ready" && <StartCard user={user} onStart={start} />}
      {phase === "over" && <ResultCard result={result} onAgain={start} />}
    </div>
  );
}

function StartCard({ user, onStart }) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center p-5">
      <div className="pz-card max-w-sm w-full text-center pz-rise" data-testid="bp-start-card">
        <div className="pz-kicker">Jixxeo PlayZone</div>
        <h1 className="pz-title">Balloon Pop</h1>
        <p className="text-white/70 text-sm mt-2">Tap balloons before they float away. Chain quick pops for combos up to x5.</p>
        <div className="grid grid-cols-3 gap-2 my-5 text-xs">
          <div className="pz-rule"><span className="pz-dot bg-[#FF2E88]" />+10</div>
          <div className="pz-rule"><span className="pz-dot bg-[#FFC83D]" />+50</div>
          <div className="pz-rule"><span className="pz-dot bg-[#14141c] ring-2 ring-[#ff2b4a]" />−30</div>
        </div>
        <button onClick={onStart} className="pz-btn w-full" data-testid="bp-start-btn"><Play className="w-5 h-5" fill="currentColor" /> Play 60s round</button>
        {user ? <p className="text-xs text-white/60 mt-3 flex items-center justify-center gap-1.5"><Coins className="w-3.5 h-3.5 text-[#FFB800]" />Earn up to 15 coins a round into your Jixxeo wallet</p>
          : <p className="text-xs text-white/70 mt-3" data-testid="bp-guest-note">Playing as guest. <Link to="/login?next=/playzone/balloon-pop" className="underline text-[#00E5FF]">Sign in</Link> to earn coins and rank.</p>}
        <Link to="/playzone#leaderboard" className="text-xs text-white/60 mt-3 inline-flex items-center gap-1 hover:text-white" data-testid="bp-leaderboard-link"><Trophy className="w-3.5 h-3.5" />Leaderboards</Link>
      </div>
    </div>
  );
}

